// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import {
  EditIcon,
  FileImage,
  Undo,
  Redo,
  Copy,
  Clipboard,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronUp,
  ChevronDown,
  Minus,
  ZoomIn,
  ZoomOut,
  Layers,
  Upload,
  Download,
  Pentagon,
  Maximize2,
  Wand2,
} from "lucide-react";
import { LAYER_CONFIG, PARK_OBJECTS } from "../../utils/scenarioEditorUtils";

export default function TopControls({
  scenarioMetadata,
  setShowScenarioSettings,
  backgroundImage,
  currentLayer,
  canUndo,
  canRedo,
  undo,
  redo,
  copyElements,
  pasteElements,
  hasSelection,
  clipboard,
  canAlign,
  alignElements,
  hasSelection: hasSelectionForZOrder,
  changeZOrder,
  svgZoom,
  handleButtonZoom,
  handleFitToView,
  setShowGroupManager,
  selectedElements,
  copySVGToClipboard,
  setShowPasteSVGDialog,
  exportSVG,
  setShowOptimizeModal,
  isCreatingPolygon,
  polygonPoints,
  completePolygon,
  tool,
  availableScenarios = [],
  scenarioId,
  onScenarioChange,
  spacesLoading = false,
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-3 flex items-center justify-between flex-wrap gap-2 min-w-0">
      <div className="flex items-center space-x-4 flex-wrap gap-2 min-w-0">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
          {scenarioMetadata.name}
        </h1>
        <button
          onClick={() => setShowScenarioSettings(true)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
          title="Scenario Settings"
        >
          <EditIcon className="w-4 h-4" />
        </button>
        <div className="flex items-center space-x-2 bg-gray-50 dark:bg-slate-700/50 px-3 py-1 rounded-lg border dark:border-slate-600">
          <span className="text-sm text-gray-600 dark:text-slate-400">
            GPS:
          </span>
          {scenarioMetadata.coordinateSystem === "gps" ? (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Active ({scenarioMetadata.scale?.toFixed(2)} m/px)
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-gray-400 dark:bg-slate-500 rounded-full" />
              <span className="text-sm font-medium text-gray-600 dark:text-slate-400">
                Need 2 GPS pins
              </span>
            </div>
          )}
        </div>
        {backgroundImage && (
          <div className="flex items-center space-x-2 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-lg border border-green-200 dark:border-green-800">
            <FileImage className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Background:
            </span>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              {backgroundImage.source === "url" ? "URL" : "File"} Loaded
            </span>
          </div>
        )}
        <div className="flex items-center space-x-2 bg-gray-50 dark:bg-slate-700/50 px-3 py-1 rounded-lg border dark:border-slate-600">
          <span className="text-sm text-gray-600 dark:text-slate-400">
            Active Layer:
          </span>
          {currentLayer ? (
            <div className="flex items-center space-x-1">
              {React.createElement(LAYER_CONFIG[currentLayer].icon, {
                className: "w-4 h-4 text-blue-600 dark:text-blue-400",
              })}
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {LAYER_CONFIG[currentLayer].name}
              </span>
            </div>
          ) : (
            <span className="text-sm font-medium text-red-600 dark:text-red-400">
              None Selected
            </span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={undo}
            disabled={!canUndo}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 text-gray-600 dark:text-slate-400"
            title="Undo (Ctrl+Z)"
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 text-gray-600 dark:text-slate-400"
            title="Redo (Ctrl+Y)"
          >
            <Redo className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={copyElements}
            disabled={!hasSelection}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 text-gray-600 dark:text-slate-400"
            title="Copy (Ctrl+C)"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={pasteElements}
            disabled={clipboard.length === 0}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:text-gray-400 dark:disabled:text-slate-500 text-gray-600 dark:text-slate-400"
            title="Paste (Ctrl+V)"
          >
            <Clipboard className="w-4 h-4" />
          </button>
        </div>
        {canAlign && (
          <div className="flex items-center space-x-1 border-l dark:border-slate-600 pl-4">
            <button
              onClick={() => alignElements("left")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => alignElements("center")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={() => alignElements("right")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => alignElements("top")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Top"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => alignElements("middle")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Middle"
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              onClick={() => alignElements("bottom")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Align Bottom"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
        {hasSelectionForZOrder && (
          <div className="flex items-center space-x-1 border-l dark:border-slate-600 pl-4">
            <button
              onClick={() => changeZOrder("front")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Bring to Front"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => changeZOrder("back")}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
              title="Send to Back"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-center space-x-2 border-l dark:border-slate-600 pl-4">
          <span className="text-xs text-gray-500 dark:text-slate-400">
            Canvas Zoom:
          </span>
          <button
            onClick={() => handleButtonZoom("out")}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
            title="Zoom Out (SVG Canvas)"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 dark:text-slate-400 min-w-[60px] text-center">
            {Math.round(svgZoom * 100)}%
          </span>
          <button
            onClick={() => handleButtonZoom("in")}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
            title="Zoom In (SVG Canvas)"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={handleFitToView}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400"
            title="Fit to View"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Scenario Selector — replaces the legacy "Group" selector. The
            scenario IS the working group in this editor; switching scenarios
            navigates to /admin/scenario-editor/{id} and reseeds the canvas
            with that scenario's spaces. */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600 dark:text-slate-400">
            Szenario:
          </label>
          <select
            value={scenarioId ?? ""}
            onChange={(e) =>
              onScenarioChange && onScenarioChange(e.target.value)
            }
            className="px-3 py-1 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-slate-200 min-w-[220px]"
          >
            <option value="">— kein Szenario (Entwurf) —</option>
            {availableScenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.scenario_code || `Szenario ${s.id}`}
              </option>
            ))}
          </select>
          {scenarioId && spacesLoading && (
            <span className="text-xs text-gray-500 dark:text-slate-400">
              Lade Flächen…
            </span>
          )}
          <button
            onClick={() => setShowGroupManager(true)}
            className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
            title="Manage Groups"
          >
            <Layers className="w-4 h-4" />
          </button>
          <div className="flex items-center space-x-2">
            <div className="text-sm text-gray-600 dark:text-slate-400">
              {hasSelection && <span>{selectedElements.length} selected</span>}
            </div>
            <div className="flex items-center space-x-1 border-l dark:border-slate-600 pl-2">
              <button
                id="copy-svg-btn"
                onClick={copySVGToClipboard}
                className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Copy SVG to Clipboard (Ctrl+Shift+C)"
              >
                <Clipboard className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowPasteSVGDialog(true)}
                className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Paste SVG Code (Ctrl+Shift+V)"
              >
                <Upload className="w-5 h-5" />
              </button>
              <button
                onClick={exportSVG}
                className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                title="Download SVG File"
              >
                <Download className="w-5 h-5" />
              </button>
              {setShowOptimizeModal && (
                <button
                  onClick={() => setShowOptimizeModal(true)}
                  className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  title="Optimize SVG (path-first export)"
                >
                  <Wand2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
        {isCreatingPolygon && (
          <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
            <Pentagon className="w-4 h-4" />
            <span className="text-sm">
              Creating polygon ({polygonPoints.length} points) - Double-click to
              finish
            </span>
            {currentLayer && (
              <div className="text-xs bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded dark:border dark:border-blue-800">
                Target: {LAYER_CONFIG[currentLayer]?.name}
              </div>
            )}
            <button
              onClick={completePolygon}
              className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-200"
            >
              Finish
            </button>
          </div>
        )}
        {Object.keys(PARK_OBJECTS).includes(tool) && (
          <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
            {React.createElement(PARK_OBJECTS[tool].icon, {
              className: "w-4 h-4",
            })}
            <span className="text-sm">
              Click to place {PARK_OBJECTS[tool].label}
            </span>
            {currentLayer ? (
              <div className="text-xs bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded dark:border dark:border-green-800">
                Target: {LAYER_CONFIG[currentLayer]?.name}
              </div>
            ) : (
              <div className="text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1 rounded dark:border dark:border-red-800">
                ⚠️ Select a layer first!
              </div>
            )}
          </div>
        )}
        {["rectangle", "circle", "text", "pin", "polygon"].includes(tool) && (
          <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
            <span className="text-sm">Click to create {tool}</span>
            {currentLayer ? (
              <div className="text-xs bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded dark:border dark:border-blue-800">
                Target: {LAYER_CONFIG[currentLayer]?.name}
              </div>
            ) : (
              <div className="text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-2 py-1 rounded dark:border dark:border-red-800">
                ⚠️ Select a layer first!
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
